/**
 * Suppress Twilio Voice SDK console logs
 *
 * The Twilio SDK uses internal loggers that bypass the logLevel configuration
 * in development mode. This utility intercepts console methods and filters out
 * Twilio-specific log messages.
 *
 * IMPORTANT: Only suppresses debug/info logs. Error and warning messages with
 * important error codes (like "Invalid Phone Number", "Call Failed", etc.) are
 * still shown for debugging purposes.
 */

const originalConsoleLog = console.log;
const originalConsoleDebug = console.debug;
const originalConsoleInfo = console.info;
const originalConsoleWarn = console.warn;
const originalConsoleError = console.error;

const isTwilioLog = (args: any[]): boolean => {
  return args.some(arg =>
    typeof arg === 'string' && (
      arg.includes('[TwilioVoice]') ||
      arg.includes('[Twilio') ||
      arg.includes('TwilioVoice')
    )
  );
};

const isImportantTwilioLog = (args: any[]): boolean => {
  // Allow these important error/warning keywords through
  const importantKeywords = [
    'Error',
    'Failed',
    'Invalid',
    'Cannot',
    'Rejected',
    'Denied',
    'Warning:',
    'error',
    'failed',
    'invalid',
  ];

  return args.some(arg =>
    typeof arg === 'string' && importantKeywords.some(keyword => arg.includes(keyword))
  );
};

export const suppressTwilioLogs = () => {
  // Override console.log - suppress Twilio debug logs
  console.log = (...args: any[]) => {
    if (!isTwilioLog(args)) {
      originalConsoleLog.apply(console, args);
    }
  };

  // Override console.debug - suppress Twilio debug logs
  console.debug = (...args: any[]) => {
    if (!isTwilioLog(args)) {
      originalConsoleDebug.apply(console, args);
    }
  };

  // Override console.info - suppress Twilio info logs
  console.info = (...args: any[]) => {
    if (!isTwilioLog(args)) {
      originalConsoleInfo.apply(console, args);
    }
  };

  // Override console.warn - allow important warnings through
  console.warn = (...args: any[]) => {
    if (!isTwilioLog(args) || isImportantTwilioLog(args)) {
      originalConsoleWarn.apply(console, args);
    }
  };

  // Override console.error - ALWAYS allow errors through (never suppress)
  console.error = (...args: any[]) => {
    originalConsoleError.apply(console, args);
  };
};

export const restoreConsoleLogs = () => {
  console.log = originalConsoleLog;
  console.debug = originalConsoleDebug;
  console.info = originalConsoleInfo;
  console.warn = originalConsoleWarn;
  console.error = originalConsoleError;
};
